// src/stores/useAIOnDemandStore.js

import { create } from 'zustand';
import { fetchWithAuth } from '../api/fetchWithAuth';

export const useAIOnDemandStore = create((set, get) => ({
  // ── Selection state ──────────────────────────────────────────
  selectedActionId: null,
  previewLayers: [],

  // Explicit imagery source chosen for the active action's run (wellhead
  // detection). Null = no selection yet. The run payload sends this as
  // dataset_id; the backend hard-validates entitlement + coverage.
  selectedDatasetId: null,

  selectedJobId: null,
  jobLayerVisible: {},

  // ── Jobs cache ──────────────────────────────────────────────
  jobs: [],
  jobsLoading: false,
  jobsError: null,

  archivedJobs: [],
  archivedJobsLoading: false,

  // ── Sync run state (for kind: 'report') ─────────────────────
  syncRun: null,

  // ── Async launch ack (for kind: 'model_run') ────────────────
  recentLaunch: null,

  // ── Selection actions ───────────────────────────────────────
  selectAction: (actionId, previewLayers = []) => set({
    selectedActionId: actionId,
    selectedJobId: null,
    syncRun: null,
    recentLaunch: null,
    previewLayers,
    selectedDatasetId: null,   // reset imagery choice when switching actions
  }),

  clearActionSelection: () => set({
    selectedActionId: null,
    syncRun: null,
    recentLaunch: null,
    previewLayers: [],
    selectedDatasetId: null,
  }),

  setSelectedDatasetId: (datasetId) => set({ selectedDatasetId: datasetId }),

  selectJob: (jobId) => set({
    selectedJobId: jobId,
    selectedActionId: null,
    syncRun: null,
    previewLayers: [],
  }),

  clearJobSelection: () => set({ selectedJobId: null }),

  toggleJobLayer: (jobId) => set((s) => ({
    jobLayerVisible: {
      ...s.jobLayerVisible,
      [jobId]: !s.jobLayerVisible[jobId],
    },
  })),

  setJobLayerVisible: (jobId, visible) => set((s) => ({
    jobLayerVisible: { ...s.jobLayerVisible, [jobId]: visible },
  })),

  // ── Sync run lifecycle ──────────────────────────────────────
  startSyncRun: (actionId) => set({
    syncRun: {
      actionId, status: 'running',
      result: null, error: null,
      startedAt: Date.now(), finishedAt: null,
    },
  }),

  completeSyncRun: (result) => set((s) => ({
    syncRun: s.syncRun ? { ...s.syncRun, status: 'complete', result, finishedAt: Date.now() } : null,
  })),

  failSyncRun: (error) => set((s) => ({
    syncRun: s.syncRun ? {
      ...s.syncRun, status: 'failed',
      error: String(error?.message ?? error),
      finishedAt: Date.now(),
    } : null,
  })),

  resetSyncRun: () => set({ syncRun: null }),

  // ── Async launch ack ────────────────────────────────────────
  recordLaunch: (actionId, jobId) => set({
    recentLaunch: { actionId, jobId, at: Date.now() },
  }),

  clearLaunch: () => set({ recentLaunch: null }),

  // ── Jobs cache: fetching ────────────────────────────────────
  loadJobs: async (projectId) => {
    if (!projectId) return;
    set({ jobsLoading: true, jobsError: null });
    try {
      const url = `/api/v1/jobs?project_id=${encodeURIComponent(projectId)}&mine=true&limit=50`;
      const res = await fetchWithAuth(url);
      if (!res.ok) throw new Error(`Failed to load jobs (${res.status})`);
      const data = await res.json();
      set({ jobs: data.jobs ?? [], jobsLoading: false });
    } catch (err) {
      console.error('[useAIOnDemandStore] loadJobs failed', err);
      set({ jobsLoading: false, jobsError: String(err?.message ?? err) });
    }
  },

  refreshJobs: async (projectId) => {
    if (!projectId) return;
    try {
      const url = `/api/v1/jobs?project_id=${encodeURIComponent(projectId)}&mine=true&limit=50`;
      const res = await fetchWithAuth(url);
      if (!res.ok) return;
      const data = await res.json();
      set({ jobs: data.jobs ?? [] });
    } catch (err) {
      console.error('[useAIOnDemandStore] refreshJobs failed', err);
    }
  },

  loadArchivedJobs: async (projectId) => {
    if (!projectId) return;
    set({ archivedJobsLoading: true });
    try {
      const url = `/api/v1/jobs?project_id=${encodeURIComponent(projectId)}&mine=true&limit=50&archived=true`;
      const res = await fetchWithAuth(url);
      if (!res.ok) throw new Error(`Failed to load archived jobs (${res.status})`);
      const data = await res.json();
      set({ archivedJobs: data.jobs ?? [], archivedJobsLoading: false });
    } catch (err) {
      console.error('[useAIOnDemandStore] loadArchivedJobs failed', err);
      set({ archivedJobsLoading: false });
    }
  },

  archiveJob: async (jobId) => {
    try {
      const res = await fetchWithAuth(`/api/v1/jobs/${jobId}/archive`, { method: 'POST' });
      if (!res.ok) throw new Error(`Archive failed (${res.status})`);
      // Remove from active jobs locally; refresh archived next time it's loaded
      set((s) => ({
        jobs: s.jobs.filter((j) => j.id !== jobId),
        archivedJobs: [],  // invalidate cache so next load is fresh
        jobLayerVisible: { ...s.jobLayerVisible, [jobId]: false },  // hide layer
      }));
      return true;
    } catch (err) {
      console.error('[useAIOnDemandStore] archiveJob failed', err);
      return false;
    }
  },

  unarchiveJob: async (jobId) => {
    try {
      const res = await fetchWithAuth(`/api/v1/jobs/${jobId}/unarchive`, { method: 'POST' });
      if (!res.ok) throw new Error(`Unarchive failed (${res.status})`);
      set((s) => ({
        archivedJobs: s.archivedJobs.filter((j) => j.id !== jobId),
        jobs: [],  // invalidate active cache so next load refetches
      }));
      return true;
    } catch (err) {
      console.error('[useAIOnDemandStore] unarchiveJob failed', err);
      return false;
    }
  },

  deleteJob: async (jobId) => {
    try {
      const res = await fetchWithAuth(`/api/v1/jobs/${jobId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      set((s) => ({
        jobs: s.jobs.filter((j) => j.id !== jobId),
        archivedJobs: s.archivedJobs.filter((j) => j.id !== jobId),
        jobLayerVisible: { ...s.jobLayerVisible, [jobId]: false },
        selectedJobId: s.selectedJobId === jobId ? null : s.selectedJobId,
      }));
      return true;
    } catch (err) {
      console.error('[useAIOnDemandStore] deleteJob failed', err);
      return false;
    }
  },

  appendJob: (job) => set((s) => ({
    jobs: [job, ...s.jobs.filter((j) => j.id !== job.id)],
  })),
}));